import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, PenLine, ShoppingBag, Upload, X, ZoomIn } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useApi } from "../utils/Api";
import PersonalisationModal from "../components/PersonalisationModal";
import SEO from "../components/SEO";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const INITIAL_VISIBLE_OPTIONS = 6;
const PHOTO_PRINT_CATEGORY_ID = 2;
const KNOWN_HEADING =
  /^(key features|features|key benefits|benefits|highlights|what'?s included|includes|specifications|specs|product details|details|why you'll love it)\s*:?\s*$/i;
const BULLET = /^[-\u2022\u00b7*\u2713\u2714]\s*/;

function isHeading(line) {
  if (!line || line.length > 40) return false;
  return KNOWN_HEADING.test(line) || /^[A-Za-z][A-Za-z '&-]{2,38}:$/.test(line);
}

// The admin description is plain typed text, so we pull the structure out of
// it. Order of attempts: a heading line ("Key Features", "Benefits:" ...)
// followed by lines; bullet-style lines; a trailing block of short lines.
// If nothing matches, the whole text is shown as normal paragraphs.
function parseDetails(text) {
  const lines = String(text || "")
    .replace(/\r/g, "")
    .split("\n")
    .map((line) => line.trim());

  const toParagraphs = (list) => {
    const paragraphs = [];
    let current = [];
    list.forEach((line) => {
      if (!line) {
        if (current.length) paragraphs.push(current.join("\n"));
        current = [];
      } else {
        current.push(line);
      }
    });
    if (current.length) paragraphs.push(current.join("\n"));
    return paragraphs;
  };

  // 1. heading followed by at least one line
  const headingIndex = lines.findIndex(
    (line, i) => isHeading(line) && lines.slice(i + 1).some(Boolean),
  );

  if (headingIndex !== -1) {
    const features = lines
      .slice(headingIndex + 1)
      .filter(Boolean)
      .map((line) => line.replace(BULLET, ""));

    return {
      intro: toParagraphs(lines.slice(0, headingIndex)),
      features,
      featuresTitle: /^key features/i.test(lines[headingIndex])
        ? "Key features"
        : lines[headingIndex].replace(/:$/, ""),
    };
  }

  // 2. bullet-style lines anywhere in the text
  const bulletLines = lines.filter((line) => BULLET.test(line));
  if (bulletLines.length >= 2) {
    return {
      intro: toParagraphs(lines.filter((line) => !BULLET.test(line))),
      features: bulletLines.map((line) => line.replace(BULLET, "")),
      featuresTitle: "Key features",
    };
  }

  // 3. last block of three or more short lines after at least one paragraph
  const blocks = [];
  let block = [];
  lines.forEach((line) => {
    if (!line) {
      if (block.length) blocks.push(block);
      block = [];
    } else {
      block.push(line);
    }
  });
  if (block.length) blocks.push(block);

  const last = blocks[blocks.length - 1];
  if (
    blocks.length > 1 &&
    last.length >= 3 &&
    last.every((line) => line.length <= 80)
  ) {
    return {
      intro: toParagraphs(blocks.slice(0, -1).flatMap((b2) => [...b2, ""])),
      features: last,
      featuresTitle: "Key features",
    };
  }

  return { intro: toParagraphs(lines), features: [], featuresTitle: "Key features" };
}

const UPLOADS_BASE_URL =
  process.env.REACT_APP_UPLOADS_BASE_URL || "https://kinewsstudiolimited.co.uk";

function buildUploadedFileUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${UPLOADS_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
}

function getPrimaryImage(product) {
  const image = product.image_url;

  if (!image) {
    return "/placeholder-product.jpg";
  }

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  return `https://kinewsstudiolimited.co.uk/${image.replace(/^\/+/, "")}`;
}

function getDefaultSelections(groups) {
  const selections = {};

  (groups || []).forEach((group) => {
    const values = (group.values || []).filter(
      (value) => Number(value.is_active) === 1,
    );
    if (!values.length) return;

    const defaultValue =
      values.find((v) => Number(v.is_default) === 1) || values[0];

    selections[group.id] = defaultValue;
  });

  return selections;
}

function getDefaultAddonSelections(groups) {
  const selections = {};

  (groups || []).forEach((group) => {
    selections[group.id] = [];
  });

  return selections;
}

function getSelectedTotal(product, selectedOptions, selectedAddons) {
  let baseOptionPrice = Number(product.base_price || 0);

  const activeGroups = (product.option_groups || []).filter(
    (group) => Number(group.is_active) === 1,
  );

  for (const group of activeGroups) {
    const selected = selectedOptions[group.id];

    if (selected) {
      const selectedPrice = Number(selected.price);
      if (!Number.isNaN(selectedPrice) && selectedPrice > 0) {
        baseOptionPrice = selectedPrice;
        break;
      }
    }

    const activeValues = (group.values || []).filter(
      (value) => Number(value.is_active) === 1,
    );

    const defaultValue =
      activeValues.find((value) => Number(value.is_default) === 1) ||
      activeValues.find((value) => {
        const price = Number(value.price);
        return !Number.isNaN(price) && price > 0;
      });

    if (defaultValue) {
      const defaultPrice = Number(defaultValue.price);
      if (!Number.isNaN(defaultPrice) && defaultPrice > 0) {
        baseOptionPrice = defaultPrice;
        break;
      }
    }
  }

  let addonsPrice = 0;
  const activeAddons = (product.addons || []).filter(
    (group) => Number(group.is_active) === 1,
  );

  for (const group of activeAddons) {
    const selectedValues = selectedAddons[group.id] || [];
    for (const selected of selectedValues) {
      const selectedPrice = Number(selected.price);
      if (!Number.isNaN(selectedPrice) && selectedPrice > 0) {
        addonsPrice += selectedPrice;
      }
    }
  }

  return baseOptionPrice + addonsPrice;
}

export default function ShopProductDetails() {
  const { id } = useParams();
  const { apiFetch } = useApi();
  const { addItem } = useCart();

  const fileInputRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedAddons, setSelectedAddons] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedAddons, setExpandedAddons] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [customText, setCustomText] = useState("");
  const [showPersonaliseModal, setShowPersonaliseModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "" });
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!lightboxOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxOpen]);

  const showToast = (message) => {
    setToast({ show: true, message });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => {
      setToast({ show: false, message: "" });
    }, 2200);
  };

  useEffect(() => {
    let mounted = true;

    const loadProduct = async () => {
      try {
        setLoading(true);
        setLoadError("");

        const res = await apiFetch("get_public_shop_product", {
          id: Number(id),
        });

        if (!res?.success) {
          throw new Error(res?.message || "Failed to load product.");
        }

        if (!mounted) return;

        setProduct(res.data);
        setSelectedOptions(getDefaultSelections(res.data.option_groups));
        setSelectedAddons(getDefaultAddonSelections(res.data.addons));
        setExpandedGroups({});
        setExpandedAddons({});
        setUploadedFiles([]);
        setCustomText("");
        setShowPersonaliseModal(false);
      } catch (err) {
        if (!mounted) return;
        setLoadError(err.message || "Unable to load product.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProduct();

    return () => {
      mounted = false;
    };
  }, [apiFetch, id]);

  const updateSelectedOption = (groupId, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [groupId]: value,
    }));
  };

  const toggleSelectedAddon = (groupId, value) => {
    setSelectedAddons((prev) => {
      const currentValues = prev[groupId] || [];
      const isSelected = currentValues.some((v) => v.id === value.id);
      if (isSelected) {
        return {
          ...prev,
          [groupId]: currentValues.filter((v) => v.id !== value.id),
        };
      } else {
        return {
          ...prev,
          [groupId]: [...currentValues, value],
        };
      }
    });
  };

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const toggleAddonExpanded = (groupId) => {
    setExpandedAddons((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const requiresPhotoUpload = Number(product?.requires_photo_upload) === 1;
  const allowsCustomText = Number(product?.allows_custom_text) === 1;
  const customTextLabel = product?.custom_text_label?.trim() || "Your text";
  const customTextMaxLength = Number(product?.custom_text_max_length || 0);
  const isPhotoPrintProduct =
    Number(product?.category_id) === PHOTO_PRINT_CATEGORY_ID;

  const handleUploadClick = () => {
    if (!product || !requiresPhotoUpload || uploadingFiles) return;
    fileInputRef.current?.click();
  };

  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const res = await apiFetch("upload_shop_temp_file", formData, {
      isFormData: true,
    });

    if (!res?.success || !res?.file_path) {
      throw new Error(res?.message || `Failed to upload ${file.name}`);
    }

    const persistentUrl = buildUploadedFileUrl(res.file_path);

    return {
      name: file.name,
      type: file.type || "",
      preview: persistentUrl,
      file_path: res.file_path,
      temp_path: res.file_path,
      uploaded: true,
    };
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    const maxUploads = Number(product?.max_uploads || 1);

    if (files.length === 0) return;

    const remainingSlots = Math.max(0, maxUploads - uploadedFiles.length);

    if (remainingSlots <= 0) {
      showToast(
        `You can upload a maximum of ${maxUploads} image${maxUploads > 1 ? "s" : ""}`,
      );
      e.target.value = "";
      return;
    }

    if (files.length > remainingSlots) {
      showToast(
        `You can only add ${remainingSlots} more image${remainingSlots > 1 ? "s" : ""}`,
      );
      e.target.value = "";
      return;
    }

    try {
      setUploadingFiles(true);

      const uploadedBatch = [];

      for (const file of files) {
        const uploaded = await uploadSingleFile(file);
        uploadedBatch.push(uploaded);
      }

      setUploadedFiles((prev) => [...prev, ...uploadedBatch]);

      showToast(
        `${uploadedBatch.length} image${uploadedBatch.length > 1 ? "s" : ""} uploaded`,
      );
    } catch (err) {
      showToast(err.message || "Failed to upload image");
    } finally {
      setUploadingFiles(false);
      e.target.value = "";
    }
  };

  const removeUploadedFile = (indexToRemove) => {
    setUploadedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const unitPrice = useMemo(() => {
    return product ? getSelectedTotal(product, selectedOptions, selectedAddons) : 0;
  }, [product, selectedOptions, selectedAddons]);

  const derivedQty = useMemo(() => {
    if (isPhotoPrintProduct) {
      return uploadedFiles.length;
    }
    return 1;
  }, [isPhotoPrintProduct, uploadedFiles]);

  const displayTotal = useMemo(() => {
    if (isPhotoPrintProduct) {
      return Number(unitPrice || 0) * Number(derivedQty || 0);
    }
    return Number(unitPrice || 0);
  }, [isPhotoPrintProduct, unitPrice, derivedQty]);

  const validateSelections = () => {
    if (!product) return false;

    const requiredGroups = (product.option_groups || []).filter(
      (group) => Number(group.is_required) === 1,
    );

    for (const group of requiredGroups) {
      if (!selectedOptions[group.id]) {
        showToast(`Please select ${group.name}`);
        return false;
      }
    }

    return true;
  };

  const validatePersonalisation = () => {
    if (!product) return false;

    if (requiresPhotoUpload && uploadedFiles.length === 0) {
      showToast("Please upload your photos first");
      return false;
    }

    if (uploadingFiles) {
      showToast("Please wait for image upload to finish");
      return false;
    }

    if (allowsCustomText) {
      const trimmed = customText.trim();

      if (!trimmed) {
        showToast(`Please enter ${customTextLabel.toLowerCase()}`);
        return false;
      }

      if (customTextMaxLength > 0 && trimmed.length > customTextMaxLength) {
        showToast(`Maximum ${customTextMaxLength} characters allowed`);
        return false;
      }
    }

    return true;
  };

  const buildCartPayload = () => {
    const flatOptions = Object.entries(selectedOptions).map(([groupId, value]) => {
      const group = product.option_groups?.find((g) => String(g.id) === String(groupId));
      return {
        id: value.id,
        group_name: group?.name || 'Option',
        label: value.label,
        price: Number(value.price || 0),
      };
    });
    
    const flatAddons = Object.entries(selectedAddons).flatMap(([groupId, values]) => {
      const group = product.addons?.find((g) => String(g.id) === String(groupId));
      return values.map((value) => ({
        id: value.id,
        group_name: group?.name || 'Add-on',
        label: value.label,
        price: Number(value.price || 0),
      }));
    });

    return {
      ...product,
      image: getPrimaryImage(product),
      final_price: Number(unitPrice || 0),
      selected_options: [...flatOptions, ...flatAddons],
      uploaded_files: uploadedFiles.map((file) => ({
        name: file.name,
        type: file.type,
        preview: file.preview,
        file_path: file.file_path,
        temp_path: file.temp_path,
        uploaded: !!file.file_path,
      })),
      custom_text: allowsCustomText ? customText.trim() : "",
      qty: isPhotoPrintProduct ? uploadedFiles.length : 1,
      quantity_locked: isPhotoPrintProduct ? 1 : 0,
    };
  };

  const resetPersonalisationState = () => {
    setUploadedFiles([]);
    setCustomText("");
    setShowPersonaliseModal(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const addToCartHandler = () => {
    if (!product) return;
    if (!validateSelections()) return;

    if (requiresPhotoUpload && uploadedFiles.length === 0) {
      showToast("Please upload your photos first");
      return;
    }

    if (uploadingFiles) {
      showToast("Please wait for image upload to finish");
      return;
    }

    addItem(buildCartPayload());
    resetPersonalisationState();
    showToast(`${product.name} added to cart`);
  };

  const openPersonaliseModal = () => {
    if (!product) return;
    if (!validateSelections()) return;
    setShowPersonaliseModal(true);
  };

  const addPersonalisedToCartHandler = () => {
    if (!product) return;
    if (!validateSelections()) return;
    if (!validatePersonalisation()) return;

    addItem(buildCartPayload());
    resetPersonalisationState();
    showToast(`${product.name} added to cart`);
  };

  const isPhotoUploadIncomplete =
    requiresPhotoUpload && !allowsCustomText && uploadedFiles.length === 0;

  const trimToWord = (text, max) => {
    if (text.length <= max) return text;
    const cut = text.slice(0, max);
    return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[,.;:\s]+$/, "")}...`;
  };

  const seoDescription = product
    ? trimToWord((product.details || "").replace(/\s+/g, " ").trim(), 155) ||
      `Buy ${product.name} from Kinews Studio Limited in Rochdale. Quality prints, frames and personalised gifts.`
    : "Frames, prints and personalised gifts from Kinews Studio Limited in Rochdale.";

  const parsedDetails = parseDetails(product?.details);

  const productJsonLd =
    product && Number(product.base_price) > 0
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: getPrimaryImage(product),
          description: seoDescription,
          brand: { "@type": "Brand", name: "Kinews Studio" },
          offers: {
            "@type": "Offer",
            priceCurrency: "GBP",
            price: Number(product.base_price).toFixed(2),
            availability: "https://schema.org/InStock",
            url: `https://kinewsstudiolimited.co.uk/shop/${id}`,
          },
        }
      : undefined;

  return (
    <main className="min-h-screen bg-[#fff7fb] pt-28 text-black md:pt-40">
      <SEO
        title={
          product
            ? `${product.name} | Kinews Studio Shop`
            : "Shop | Kinews Studio"
        }
        description={seoDescription}
        image={product ? getPrimaryImage(product) : undefined}
        noindex={!loading && !product}
        jsonLd={productJsonLd}
      />
      <div
        className={[
          "fixed left-1/2 top-5 z-[60] -translate-x-1/2 transition-all duration-200",
          toast.show
            ? "translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-2 opacity-0",
        ].join(" ")}
        aria-live="polite"
      >
        <div className="rounded-2xl bg-green-500 px-4 py-3 text-sm font-semibold text-white shadow-lg">
          {toast.message}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16 xl:px-6 pb-16">
        <nav
          aria-label="Breadcrumb"
          className="mb-6 flex items-center gap-2 text-sm text-black/50"
        >
          <Link to="/shop" className="font-medium transition hover:text-pink-600">
            Shop
          </Link>
          {product?.name && (
            <>
              <span aria-hidden="true">/</span>
              <span className="truncate text-black/80">{product.name}</span>
            </>
          )}
        </nav>

        {loading ? (
          <p>Loading...</p>
        ) : loadError ? (
          <p className="text-red-600">{loadError}</p>
        ) : !product ? (
          <p>Product not found</p>
        ) : (
          <>
            <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
              <div className="relative overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_16px_40px_rgba(0,0,0,0.06)] lg:min-h-[480px]">
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  aria-label="View larger image"
                  className="group block aspect-square w-full cursor-zoom-in bg-black/5 lg:absolute lg:inset-0 lg:aspect-auto"
                >
                  <img
                    src={getPrimaryImage(product)}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#1c1c1c] shadow-md transition group-hover:bg-white">
                    <ZoomIn size={18} aria-hidden="true" />
                  </span>
                </button>
              </div>

              <div className="rounded-3xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)] md:p-8">
                <h1 className="text-3xl font-semibold leading-tight text-[#2d2d2d] md:text-4xl">
                  {product.name}
                </h1>

                {/* ── OPTIONS ── */}
                {(product.option_groups || []).filter(
                  (group) => Number(group.is_active) === 1,
                ).length > 0 && (
                  <div className="mt-7 space-y-6">
                    
                    {/* Section divider label */}
                    <div className="flex items-center gap-3">
                      <span className="h-px flex-1 bg-black/10" />
                      <p className="text-[11px] font-bold uppercase tracking-widest text-black/35">
                        Choose your options
                      </p>
                      <span className="h-px flex-1 bg-black/10" />
                    </div>

                    {(product.option_groups || [])
                      .filter((group) => Number(group.is_active) === 1)
                      .map((group) => {
                        const activeValues = (group.values || []).filter(
                          (value) => Number(value.is_active) === 1,
                        );

                        const isExpanded = !!expandedGroups[group.id];
                        const shouldCollapse =
                          activeValues.length > INITIAL_VISIBLE_OPTIONS;

                        const visibleValues =
                          shouldCollapse && !isExpanded
                            ? activeValues.slice(0, INITIAL_VISIBLE_OPTIONS)
                            : activeValues;


                        return (
                          <div key={group.id}>
                            <p className="mb-3 text-sm font-semibold text-[#2d2d2d]">
                              {group.name}
                            </p>

                            <div className="flex flex-wrap gap-2">
                              {visibleValues.map((value) => {
                                const isSelected =
                                  selectedOptions[group.id]?.id === value.id;

                                return (
                                  <button
                                    key={value.id}
                                    type="button"
                                    onClick={() =>
                                      updateSelectedOption(group.id, value)
                                    }
                                    className={[
                                      "flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-medium transition-all duration-200",
                                      isSelected
                                        ? "border-pink-500 bg-pink-100 font-semibold text-pink-700"
                                        : "border-gray-200 bg-white text-[#3a3a3a] hover:border-pink-400 hover:bg-pink-50 hover:shadow-sm",
                                    ].join(" ")}
                                  >
                                    {value.label}
                                  </button>
                                );
                              })}
                            </div>

                            {shouldCollapse && (
                              <button
                                type="button"
                                onClick={() => toggleGroupExpanded(group.id)}
                                className="mt-3 flex items-center gap-1 text-xs font-semibold text-pink-500 hover:text-pink-600"
                              >
                                {isExpanded ? (
                                  <>
                                    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
                                      <path d="M2 8l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Show fewer
                                  </>
                                ) : (
                                  <>
                                    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
                                      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Show {activeValues.length - INITIAL_VISIBLE_OPTIONS} more
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}


                {/* ── ADD-ONS ── */}
                {(product.addons || []).filter(
                  (group) => Number(group.is_active) === 1,
                ).length > 0 && (
                  <div className="mt-8 overflow-hidden rounded-2xl border border-pink-100 bg-gradient-to-br from-pink-50/60 to-white">
                    {/* Panel header */}
                    <div className="flex items-center gap-3 border-b border-pink-100 px-3 py-2">
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-pink-500 text-white shadow-sm shadow-pink-200">
                        <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M8 2l1.5 3.5L13 6.5l-2.5 2.5.5 3.5L8 11l-3 1.5.5-3.5L3 6.5l3.5-1L8 2z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-[#2d2d2d]">
                          Add-ons (Optional)
                        </p>
                      </div>
                    </div>

                    {/* Panel body */}
                    <div className="space-y-6 px-3 py-3">
                      {(product.addons || [])
                        .filter((group) => Number(group.is_active) === 1)
                        .map((group) => {
                          const activeValues = (group.values || []).filter(
                            (value) => Number(value.is_active) === 1,
                          );

                          const isExpanded = !!expandedAddons[group.id];
                          const shouldCollapse =
                            activeValues.length > INITIAL_VISIBLE_OPTIONS;

                          const visibleValues =
                            shouldCollapse && !isExpanded
                              ? activeValues.slice(0, INITIAL_VISIBLE_OPTIONS)
                              : activeValues;

                          return (
                            <div key={group.id}>
                              <p className="mb-3 text-sm font-semibold text-[#4a3a43]">
                                {group.name}
                              </p>

                              <div className="flex flex-wrap gap-2">
                                {visibleValues.map((value) => {
                                  const isSelected = (
                                    selectedAddons[group.id] || []
                                  ).some((v) => v.id === value.id);
                                  const hasPrice = Number(value.price) > 0;

                                  return (
                                    <button
                                      key={value.id}
                                      type="button"
                                      onClick={() =>
                                        toggleSelectedAddon(group.id, value)
                                      }
                                      className={[
                                        "flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all duration-200",
                                        isSelected
                                          ? "border-pink-500 bg-pink-100 font-semibold text-pink-700"
                                          : "border-pink-200 bg-white text-[#3a3a3a] hover:border-pink-400 hover:bg-pink-50 hover:shadow-sm",
                                      ].join(" ")}
                                    >
                                      {value.label}

                                      {hasPrice && (
                                        <span
                                          className={[
                                            "rounded px-2 py-0.5 text-[11px] font-semibold",
                                            isSelected
                                              ? "bg-white text-pink-600"
                                              : "bg-pink-100 text-pink-600",
                                          ].join(" ")}
                                        >
                                          +{money(value.price)}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>

                              {shouldCollapse && (
                                <button
                                  type="button"
                                  onClick={() => toggleAddonExpanded(group.id)}
                                  className="mt-3 flex items-center gap-1 text-xs font-semibold text-pink-500 hover:text-pink-600"
                                >
                                  {isExpanded ? (
                                    <>
                                      <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
                                        <path d="M2 8l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                      Show fewer
                                    </>
                                  ) : (
                                    <>
                                      <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
                                        <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                      Show {activeValues.length - INITIAL_VISIBLE_OPTIONS} more
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}


                {/* ── PRICE ── */}
                <div className="mt-8 border-t border-black/10 pt-6">
                  {isPhotoPrintProduct ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-pink-500">
                          Price per print:
                        </p>
                        <p className="text-3xl font-bold">{money(unitPrice)}</p>
                      </div>

                      {uploadedFiles.length > 0 && (
                        <div className="flex items-center justify-between rounded-2xl bg-black/[0.03] px-4 py-3">
                          <div>
                            <p className="text-sm font-semibold text-black/60">
                              Quantity
                            </p>
                            <p className="text-lg font-semibold">
                              {derivedQty}
                            </p>
                          </div>

                          <div className="text-right">
                            <p className="text-sm font-semibold text-pink-500">
                              Total
                            </p>
                            <p className="text-2xl font-bold">
                              {money(displayTotal)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-semibold text-pink-600">
                        Price
                      </p>
                      <p className="text-4xl font-bold tracking-tight">
                        {money(unitPrice)}
                      </p>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple={Number(product.max_uploads || 1) > 1}
                  onChange={handleFileChange}
                  className="hidden"
                />

                <div className="mt-6 flex flex-wrap gap-4">
                  {requiresPhotoUpload && !allowsCustomText && (
                    <button
                      type="button"
                      onClick={handleUploadClick}
                      disabled={uploadingFiles}
                      className={[
                        "inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition",
                        uploadingFiles
                          ? "cursor-not-allowed bg-pink-300"
                          : "bg-pink-500 hover:bg-pink-600",
                      ].join(" ")}
                    >
                      <Upload size={18} aria-hidden="true" />
                      {uploadingFiles ? "Uploading..." : "Upload photos"}
                    </button>
                  )}

                  {allowsCustomText ? (
                    <button
                      type="button"
                      onClick={openPersonaliseModal}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-pink-500 px-7 py-3 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition hover:bg-pink-600"
                    >
                      <PenLine size={18} aria-hidden="true" />
                      Personalise
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={addToCartHandler}
                      disabled={isPhotoUploadIncomplete || uploadingFiles}
                      className={[
                        "inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-[15px] font-semibold transition",
                        isPhotoUploadIncomplete || uploadingFiles
                          ? "cursor-not-allowed bg-black/20 text-white/80"
                          : "bg-pink-500 text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] hover:bg-pink-600",
                      ].join(" ")}
                    >
                      <ShoppingBag size={18} aria-hidden="true" />
                      {uploadingFiles ? "Uploading..." : "Add to cart"}
                    </button>
                  )}
                </div>

                {requiresPhotoUpload && !allowsCustomText && uploadingFiles && (
                  <p className="mt-4 text-sm text-black/60">
                    Uploading image...
                  </p>
                )}

                {requiresPhotoUpload &&
                  !allowsCustomText &&
                  uploadedFiles.length > 0 && (
                    <div className="mt-5">
                      <p className="mb-3 text-sm font-medium text-[#2d2d2d]">
                        Uploaded image{uploadedFiles.length > 1 ? "s" : ""}
                      </p>

                      <div className="flex flex-wrap gap-3">
                        {uploadedFiles.map((file, index) => (
                          <div
                            key={`${file.name}_${index}`}
                            className="relative h-20 w-20 overflow-hidden rounded border border-gray-200 bg-black/5"
                          >
                            <img
                              src={file.preview}
                              alt={file.name}
                              className="h-full w-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeUploadedFile(index)}
                              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white"
                              aria-label="Remove image"
                            >
                              <X size={12} strokeWidth={3} />
                            </button>
                            {(file.uploaded || file.file_path) && (
                              <span className="absolute bottom-1 right-1 h-2 w-2 rounded-full bg-green-500 ring-1 ring-white" />
                            )}
                          </div>
                        ))}
                      </div>

                      <p className="mt-2 text-xs text-black/55">
                        {uploadedFiles.length} of{" "}
                        {Number(product.max_uploads || 1)} uploaded
                      </p>
                    </div>
                  )}

              </div>
            </div>

            <div className="mt-10 rounded-3xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)] md:p-8">
              <div
                className={
                  parsedDetails.features.length > 0
                    ? "grid gap-8 md:grid-cols-[1.4fr_1fr]"
                    : ""
                }
              >
                <div>
                  <h2 className="text-2xl font-semibold">About this product</h2>

                  <div className="mt-4 space-y-4 text-[15px] leading-7 text-black/70">
                    {parsedDetails.intro.length > 0 ? (
                      parsedDetails.intro.map((paragraph, index) => (
                        <p key={index} className="whitespace-pre-line">
                          {paragraph}
                        </p>
                      ))
                    ) : (
                      <p>No additional details available.</p>
                    )}
                  </div>
                </div>

                {parsedDetails.features.length > 0 && (
                  <div className="rounded-2xl bg-[#fff7fb] p-6">
                    <h3 className="text-lg font-semibold">{parsedDetails.featuresTitle}</h3>

                    <ul className="mt-4 space-y-3">
                      {parsedDetails.features.map((feature, index) => {
                        const match = feature.match(/^([^:]{2,24}):\s*(.+)$/);

                        return (
                          <li
                            key={index}
                            className="flex items-start gap-3 text-sm leading-6 text-black/75"
                          >
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pink-100 text-pink-600">
                              <Check size={12} strokeWidth={3} />
                            </span>
                            <span>
                              {match ? (
                                <>
                                  <strong className="font-semibold text-black/85">
                                    {match[1]}:
                                  </strong>{" "}
                                  {match[2]}
                                </>
                              ) : (
                                feature
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {lightboxOpen && product && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-[#1c1c1c]/95 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={product.name}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close image"
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <X size={22} />
          </button>
          <img
            src={getPrimaryImage(product)}
            alt={product.name}
            className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <PersonalisationModal
        show={showPersonaliseModal}
        product={product}
        getPrimaryImage={getPrimaryImage}
        requiresPhotoUpload={requiresPhotoUpload}
        allowsCustomText={allowsCustomText}
        customTextLabel={customTextLabel}
        customText={customText}
        customTextMaxLength={customTextMaxLength}
        setCustomText={setCustomText}
        uploadedFiles={uploadedFiles}
        uploadingFiles={uploadingFiles}
        handleUploadClick={handleUploadClick}
        removeUploadedFile={removeUploadedFile}
        isPhotoPrintProduct={isPhotoPrintProduct}
        displayTotal={displayTotal}
        unitPrice={unitPrice}
        addPersonalisedToCartHandler={addPersonalisedToCartHandler}
        setShowPersonaliseModal={setShowPersonaliseModal}
      />
    </main>
  );
}
